// Segment service — converts a rendered chapter into speakable chunks.
//
// Strategy:
//   1. Walk the iframe document.body, find paragraph-y nodes (p, li, blockquote,
//      h1..h6, dd).
//   2. For each node, optionally subdivide into sentences using a small
//      heuristic regex. We avoid heavy NLP because the MVP just needs
//      "good enough" sentence boundaries.
//   3. Stamp each segment with a CSS selector we can use later to highlight
//      the live node from outside the iframe.

import type { TextSegment, TTSSegmentLevel } from "@/types/tts";

const SENTENCE_SPLIT = /(?<=[.!?…])\s+(?=[A-Z“"'(])/g;

const BLOCK_TAGS = new Set([
  "P",
  "DIV",
  "LI",
  "BLOCKQUOTE",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
  "DD",
]);

/** Same set as a selector, for the descendant test below. */
const BLOCK_SELECTOR = "p, div, li, blockquote, h1, h2, h3, h4, h5, h6, dd";

export function buildSegments(opts: {
  doc: Document;
  bookId: string;
  spineIndex: number;
  href: string;
  level: TTSSegmentLevel;
}): TextSegment[] {
  const { doc, bookId, spineIndex, href, level } = opts;
  const segments: TextSegment[] = [];

  // DIV is in BLOCK_TAGS deliberately (2026-10-09). Plenty of EPUBs use
  // <div> for body prose and never emit a <p> at all — QualityLand 2.0 is
  // 98.6% <div>, so read-aloud built ZERO segments for it and Play was
  // silent. But a wrapper <div> around real blocks also matches, and
  // textContent on the wrapper would repeat its children's text as one
  // giant segment: the book read twice, once in paragraphs and once in a
  // lump. So keep LEAF blocks only — a block that contains another block
  // is a container, not a paragraph.
  const all = Array.from(doc.body.querySelectorAll<HTMLElement>("*")).filter(
    (el) => BLOCK_TAGS.has(el.tagName)
  );
  const blockSet = new Set<HTMLElement>(all);
  const nodes = all.filter((el) => {
    for (const child of Array.from(el.children)) {
      if (blockSet.has(child as HTMLElement)) return false;
      if ((child as HTMLElement).querySelector?.(BLOCK_SELECTOR)) return false;
    }
    return true;
  });

  let paragraphIndex = 0;
  for (const node of nodes) {
    const text = (node.textContent ?? "").trim();
    if (!text) continue;

    // Tag the node so we can locate it later from outside the iframe.
    const tagKey = `pv-${spineIndex}-${paragraphIndex}`;
    node.setAttribute("data-pv-seg", tagKey);

    const sourceTag = node.tagName.toUpperCase();

    if (level === "paragraph") {
      segments.push({
        id: `${bookId}:${spineIndex}:${paragraphIndex}`,
        bookId,
        spineIndex,
        href,
        text,
        elementSelector: `[data-pv-seg="${tagKey}"]`,
        paragraphIndex,
        tag: sourceTag,
      });
    } else {
      const sentences = text
        .split(SENTENCE_SPLIT)
        .map((s) => s.trim())
        .filter(Boolean);
      sentences.forEach((s, sIdx) => {
        segments.push({
          id: `${bookId}:${spineIndex}:${paragraphIndex}:${sIdx}`,
          bookId,
          spineIndex,
          href,
          text: s,
          // We highlight at paragraph granularity even when speaking
          // sentence-by-sentence — sub-paragraph highlighting requires
          // splitting text nodes which is invasive and out of scope for MVP.
          elementSelector: `[data-pv-seg="${tagKey}"]`,
          paragraphIndex,
          sentenceIndex: sIdx,
          tag: sourceTag,
        });
      });
    }

    paragraphIndex++;
  }

  return segments;
}

/** Find the segment closest to a given anchor element (used when starting TTS
 *  from the user's current reading position). */
export function findNearestSegment(
  segments: TextSegment[],
  anchor: HTMLElement | null
): TextSegment | undefined {
  if (!anchor) return segments[0];
  const tag = anchor.closest("[data-pv-seg]") as HTMLElement | null;
  if (!tag) return segments[0];
  const key = tag.getAttribute("data-pv-seg");
  return segments.find((s) => s.elementSelector === `[data-pv-seg="${key}"]`);
}

/**
 * Wrap each whitespace-separated word in the given element with
 * `<span class="pv-w" data-pv-wo="START">word</span>`, where START is the
 * character offset of that word in the element's text content. Idempotent:
 * if the element has already been word-wrapped (data-pv-words-wrapped attr),
 * we don't re-wrap.
 *
 * Returns an array of {start, end, span} entries so the caller can map a
 * SpeechSynthesisUtterance boundary charIndex back to the right span and
 * highlight it.
 */
export interface WordSpan {
  start: number;
  end: number;
  el: HTMLSpanElement;
}

export function wrapWords(el: HTMLElement): WordSpan[] {
  if (el.getAttribute("data-pv-words-wrapped") === "1") {
    return Array.from(
      el.querySelectorAll<HTMLSpanElement>(".pv-w")
    ).map((sp) => {
      const start = Number(sp.getAttribute("data-pv-wo") ?? "0");
      const end = start + (sp.textContent?.length ?? 0);
      return { start, end, el: sp };
    });
  }

  const doc = el.ownerDocument;
  const spans: WordSpan[] = [];
  // Walk all text nodes inside the element and replace each one with a
  // fragment that re-injects spaces and span-wraps each word. Preserves the
  // overall whitespace structure so layout stays put.
  let runningOffset = 0;
  const walker = doc.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
  const textNodes: Text[] = [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let n: any = walker.nextNode();
  while (n) {
    textNodes.push(n as Text);
    n = walker.nextNode();
  }
  for (const node of textNodes) {
    const text = node.nodeValue ?? "";
    const frag = doc.createDocumentFragment();
    // Token regex: capture word runs and whitespace separately so we keep
    // spaces between words and don't disturb layout.
    const re = /(\s+)|(\S+)/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
      if (m[1]) {
        // whitespace
        frag.appendChild(doc.createTextNode(m[1]));
        runningOffset += m[1].length;
      } else if (m[2]) {
        const word = m[2];
        const span = doc.createElement("span");
        span.className = "pv-w";
        span.setAttribute("data-pv-wo", String(runningOffset));
        span.textContent = word;
        frag.appendChild(span);
        spans.push({
          start: runningOffset,
          end: runningOffset + word.length,
          el: span,
        });
        runningOffset += word.length;
      }
    }
    node.parentNode?.replaceChild(frag, node);
  }
  el.setAttribute("data-pv-words-wrapped", "1");
  return spans;
}
